/**
 * `Kbd` (W1-12 ui-kit-i18n; contract KbdProps): a keyboard shortcut drawn as key caps. The label comes from the
 * keymap through the UiBridge (layout-aware: "Ctrl+K", "Alt+,", "Cmd" on macOS, "(physical)" on Firefox), so the
 * primitive never formats chords itself.
 */

import { cx } from './internal/cx';
import { useUiBridge } from './bridge';
import s from './Kbd.module.css';
import type { KbdProps } from './types';

/**
 * Splits a shortcut label into keys: "Ctrl+Shift+K" -> [Ctrl, Shift, K]. A '+' that follows a separator is a
 * key of its own ("Ctrl++" -> [Ctrl, +]), and so is one after a space inside a key name ("Ctrl+Num +" ->
 * [Ctrl, Num +]: the numpad's plus); a trailing note in parentheses stays on its key ("K (physical)").
 */
export function splitShortcutLabel(label: string): string[] {
  const keys: string[] = [];
  let current = '';
  for (const ch of label) {
    if (ch === '+' && current !== '' && !current.endsWith(' ')) {
      keys.push(current.trim());
      current = '';
      continue;
    }
    current += ch;
  }
  if (current.trim() !== '') keys.push(current.trim());
  return keys;
}

export function Kbd(props: KbdProps & { readonly className?: string | undefined }) {
  const bridge = useUiBridge();
  const label = bridge.label(props.chord);
  const keys = splitShortcutLabel(label);
  return (
    <kbd className={cx(s.chord, props.className)} data-chord={props.chord}>
      {keys.map((key, i) => (
        <span key={`${String(i)}:${key}`} className={s.part}>
          {i > 0 && <span className={s.plus}>+</span>}
          <kbd className={s.key}>{key}</kbd>
        </span>
      ))}
    </kbd>
  );
}
